// src/cockpit4/components/CommandCenterStrip.tsx
// ============================================
// V1.5 P1-7：CEO Command Center 首屏指挥条
//
// 第一屏只放老板最关心的 3 件事：
//   ① 公司现在值多少钱（今日财务 + 月目标完成度）
//   ④ Hermes 正在做什么（活动流实时）
//   ⑤ 企业健康度（收入/客户/内容/现金流/产品 五维色点）
//
// 数据原则：
//   - 财务/活动流由 CEOWorkspace 传入（真实数据，不重复请求）
//   - 产品/客户计数在本组件内轻量查询（SKU stats / CRM stats），失败静默 → 黄点
//   - 无假数据：降级显示红点，0 值显示黄点并给出真实文案
// ============================================

import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Wallet,
  TrendingUp,
  Activity,
  Loader2,
  HeartPulse,
  ArrowRight,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import type { CeoSourceStatus } from "../../hooks/useDashboardData";
import type { ActivityItem } from "../../hooks/useCockpitData";
import { SKUQuery } from "../../runtime/SKURuntime/types";

// ============================================
// 类型
// ============================================

export interface CommandCenterStripProps {
  /** 今日收入（元，财务聚合真实值） */
  revenue: number;
  /** 今日毛利（元） */
  profit: number;
  /** 今日现金流（元） */
  cash: number;
  /** 今日新增客户 */
  newCustomers: number;
  /** 今日内容产出 */
  contentCount: number;
  /** 日收入目标（OKR 月目标拆解，未配置为空） */
  dailyRevenueTarget?: number;
  /** 日新增客户目标 */
  dailyCustomerTarget?: number;
  /** 逐数据源降级状态 */
  sourceStatus?: CeoSourceStatus | null;
  /** 全局降级标记 */
  degraded: boolean;
  /** Hermes 活动流（取前 N 条） */
  activities: ActivityItem[];
}

// ============================================
// 工具
// ============================================

function fmt(v: number): string {
  if (v >= 10000) return `${(v / 10000).toFixed(1)} 万`;
  return v.toLocaleString("zh-CN");
}

/** 健康度色点：绿（有产出）/ 黄（真实 0 值）/ 红（降级/查询失败） */
type Dot = { color: string; label: string };

function dotFor(
  hasData: boolean,
  degraded: boolean,
  zeroLabel: string,
  okLabel: string
): Dot {
  if (degraded) return { color: "#F87171", label: "查询失败/降级" };
  if (hasData) return { color: "#34D399", label: okLabel };
  return { color: "#FBBF24", label: zeroLabel };
}

export default function CommandCenterStrip(props: CommandCenterStripProps) {
  const { revenue, profit, cash, newCustomers, contentCount } = props;

  // 产品/客户计数（轻量查询，失败静默）
  const [skuCount, setSkuCount] = useState<number>(0);
  const [crmCount, setCrmCount] = useState<number>(0);
  useEffect(() => {
    let cancelled = false;
    const init = RuntimeManager.isInitialized();
    if (!init) return;
    const jobs: Promise<void>[] = [];
    if (RuntimeManager.hasRuntime(RuntimeName.SKU)) {
      jobs.push(
        RuntimeManager.query(RuntimeName.SKU, { type: SKUQuery.Stats } as any)
          .then((r: any) => {
            if (!cancelled) setSkuCount(Number(r?.data?.total ?? 0));
          })
          .catch(() => {})
      );
    }
    if (RuntimeManager.hasRuntime(RuntimeName.CRM)) {
      jobs.push(
        RuntimeManager.query(RuntimeName.CRM, { type: "stats" } as any)
          .then((r: any) => {
            if (!cancelled) setCrmCount(Number(r?.data?.total ?? 0));
          })
          .catch(() => {})
      );
    }
    return () => {
      cancelled = true;
    };
  }, []);

  // 月目标完成度（今日达成 / 日目标，未设目标显示真实空态）
  const progress = useMemo(() => {
    if (!props.dailyRevenueTarget || props.dailyRevenueTarget <= 0) return null;
    return Math.min(100, Math.round((revenue / props.dailyRevenueTarget) * 100));
  }, [props.dailyRevenueTarget, revenue]);

  const financeDegraded = props.degraded || !!props.sourceStatus?.finance;

  // 五维健康度
  const dims = useMemo(() => {
    const src = props.sourceStatus;
    return [
      { name: "收入", ...dotFor(revenue > 0, props.degraded || !!src?.finance, "今日 0 收入（真实）", "今日有收入") },
      { name: "客户", ...dotFor(newCustomers > 0, props.degraded || !!src?.crm, "今日 0 新客（真实）", "今日有新客") },
      { name: "内容", ...dotFor(contentCount > 0, props.degraded || !!src?.content, "今日 0 内容（真实）", "今日有内容") },
      { name: "现金流", ...dotFor(cash > 0, props.degraded || !!src?.finance, "现金 0（真实）", "现金流为正") },
      { name: "产品", ...dotFor(skuCount > 0, false, "产品库为空", `${skuCount} 个产品`) },
    ];
  }, [revenue, newCustomers, contentCount, cash, skuCount, props.degraded, props.sourceStatus]);

  const latestActivities = props.activities.slice(0, 4);

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="relative rounded-2xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/80 backdrop-blur-xl p-4"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />

      <div className="grid grid-cols-1 md:grid-cols-[1.3fr_1fr_0.9fr] gap-3">
        {/* ① 公司现在值多少钱 */}
        <div>
          <div className="flex items-center gap-1.5 mb-2.5">
            <span className="w-5 h-5 rounded-md flex items-center justify-center bg-cyan-500/15 border border-cyan-500/30">
              <Wallet size={11} className="text-cyan-400" />
            </span>
            <span className="text-[11px] font-semibold text-white tracking-tight">公司现在值多少钱</span>
            <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">Today · 今日口径</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2">
              <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-0.5">今日收入</div>
              <div className="text-[17px] font-bold text-emerald-300">¥{fmt(revenue)}</div>
            </div>
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2">
              <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-0.5">今日毛利</div>
              <div className="text-[17px] font-bold text-cyan-300">¥{fmt(profit)}</div>
            </div>
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2">
              <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-0.5">现金流</div>
              <div className="text-[17px] font-bold text-blue-300">¥{fmt(cash)}</div>
            </div>
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2">
              <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-0.5">日目标完成度</div>
              {progress === null ? (
                <div className="text-[11px] font-mono text-amber-300">未设目标</div>
              ) : (
                <div className="text-[17px] font-bold" style={{ color: progress >= 60 ? "#34D399" : progress >= 30 ? "#FBBF24" : "#F87171" }}>
                  {progress}%
                </div>
              )}
            </div>
          </div>
          {/* 目标进度条 */}
          {progress !== null && (
            <div className="mt-2 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${progress}%`,
                  background: progress >= 60 ? "#34D399" : progress >= 30 ? "#FBBF24" : "#F87171",
                }}
              />
            </div>
          )}
          {financeDegraded && (
            <div className="mt-1.5 text-[9px] font-mono text-red-400/80">财务查询降级 — 数据可能不全</div>
          )}
        </div>

        {/* ④ Hermes 正在做什么 */}
        <div className="border-t md:border-t-0 md:border-l border-white/[0.05] md:pl-3 pt-3 md:pt-0">
          <div className="flex items-center gap-1.5 mb-2.5">
            <span className="w-5 h-5 rounded-md flex items-center justify-center bg-purple-500/15 border border-purple-500/30">
              <Activity size={11} className="text-purple-400" />
            </span>
            <span className="text-[11px] font-semibold text-white tracking-tight">Hermes 正在做什么</span>
          </div>
          {latestActivities.length === 0 ? (
            <div className="text-[10px] font-mono text-gray-500 leading-relaxed">
              暂无活动记录 — Runtime 执行任务后这里实时刷新
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {latestActivities.map((a) => (
                <div key={a.id} className="flex items-start gap-1.5 text-[10px] font-mono">
                  <span
                    className="mt-[3px] w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{
                      background:
                        a.status === "success" ? "#34D399" : a.status === "failed" ? "#F87171" : "#FBBF24",
                    }}
                  />
                  <div className="min-w-0">
                    <div className="text-gray-300 truncate">{a.summary}</div>
                    <div className="text-[9px] text-gray-600">
                      {a.source || "runtime"} · {new Date(a.timestamp).toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ⑤ 企业健康度 */}
        <div className="border-t md:border-t-0 md:border-l border-white/[0.05] md:pl-3 pt-3 md:pt-0">
          <div className="flex items-center gap-1.5 mb-2.5">
            <span className="w-5 h-5 rounded-md flex items-center justify-center bg-emerald-500/15 border border-emerald-500/30">
              <HeartPulse size={11} className="text-emerald-400" />
            </span>
            <span className="text-[11px] font-semibold text-white tracking-tight">企业健康度</span>
          </div>
          <div className="flex flex-col gap-1.5">
            {dims.map((d) => (
              <div key={d.name} className="flex items-center justify-between text-[10px] font-mono">
                <span className="text-gray-400">{d.name}</span>
                <span className="flex items-center gap-1.5">
                  <span className="text-[9px] text-gray-600 max-w-[120px] truncate">{d.label}</span>
                  <span className="w-2 h-2 rounded-full" style={{ background: d.color }} />
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.section>
  );
}
